import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { skipToken } from '@reduxjs/toolkit/query';
import { UID_NAME_TUPLES } from 'constants/dataSets';
import { VALUE_CHAIN_SECTION } from 'constants/formFields';
import {
  useGetNaceCodeTreeQuery,
} from 'store/slices/dataSets/apis/dataSetsApi';
import { useGetLocationsCountriesQuery } from 'store/slices/geoLocations/apis/geoLocationsApi';
import { useUserMeQuery } from 'store/slices/user/apis/user';
import { useGetSupplierDataQuery, useUidNameTuplesVCQuery } from 'store/slices/valueChainMap/apis/valueChainMapApi';
import { selectUidNameTuplesVC } from 'store/slices/valueChainMap/selectors';
import getSupplierDefaultValuesVC from 'utils/formHelpers/getSupplierDefaultValuesVC';
import { createSupplierVCSchema } from 'utils/validators';

import type { TCreateSupplierVCSchema } from 'utils/validators';
import type ICreateSupplierForm from '../interfaces/ICreateSupplierForm';

const useCreateSupplierForm = ({
  recordId, isPageLoading, valueChainMapId, valueChainSectionCurrent,
}: ICreateSupplierForm) => {
  const { data } = useGetSupplierDataQuery(
    recordId ? { chainId: `${recordId}` } : skipToken,
    { refetchOnMountOrArgChange: true },
  );

  const { data: naceCodeTreeData } = useGetNaceCodeTreeQuery();

  const { data: profitCenterIds, isLoading: isProfitCenterIdsLoading } = useUidNameTuplesVCQuery({
    types: [UID_NAME_TUPLES.profitCenter],
    valueChainMapId,
  }, {
    selectFromResult: (result) => ({ ...result, data: selectUidNameTuplesVC(result.data) }),
  });

  const { data: userData } = useUserMeQuery();

  const companyCurrency = userData?.data?.company?.currency;

  const { data: locationsCountries } = useGetLocationsCountriesQuery();

  const {
    control,
    handleSubmit,
    formState,
    formState: { isDirty, isLoading },
    reset,
    setError,
    getValues,
    watch,
    setValue,
    resetField,
  } = useForm<TCreateSupplierVCSchema>({
    mode: 'onSubmit',
    resolver: yupResolver(createSupplierVCSchema),
    defaultValues: getSupplierDefaultValuesVC({
      values: data?.data,
      naceCodeTreeData,
      profitCenterIds,
      companyCurrency,
      locationsCountries: locationsCountries?.data || [],
      valueChainSectionCurrent,
    }),
  });

  useEffect(() => {
    if (data?.data && !isPageLoading && !isProfitCenterIdsLoading) {
      reset(getSupplierDefaultValuesVC(
        {
          values: data?.data,
          naceCodeTreeData,
          profitCenterIds,
          companyCurrency,
          locationsCountries: locationsCountries?.data || [],
          valueChainSectionCurrent,
        },
      ));
    }
  }, [reset, data, isPageLoading, naceCodeTreeData,
    isProfitCenterIdsLoading, profitCenterIds, companyCurrency,
    locationsCountries, valueChainSectionCurrent]);

  useEffect(() => {
    if (valueChainSectionCurrent && !isPageLoading && !isProfitCenterIdsLoading) {
      setValue(VALUE_CHAIN_SECTION, {
        value: valueChainSectionCurrent,
        label: valueChainSectionCurrent,
      });
    }
  }, [valueChainSectionCurrent, setValue, isPageLoading, isProfitCenterIdsLoading]);

  const fieldsName = Object.keys(getValues());

  return {
    control,
    handleSubmit,
    isDirty,
    reset,
    isLoading,
    setError,
    fieldsName,
    watch,
    setValue,
    resetField,
    profitCenterIds,
    formState,
  };
};

export default useCreateSupplierForm;
